import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQueries,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query'
import * as R from 'rambdax'
import {
  admin,
  api,
  ApiError,
  Falsy,
  IS_BROWSER,
  IS_CHROMATIC,
  logger,
  SITE_SUFFIX,
} from 'tizra'
import {TizraClient} from 'tizra/client'
import {navigationType, uptimeSeconds} from 'tizra/dom'
import {
  makeQueryParams,
  TizraApiParams,
  TizraQueryArgs,
  UseApiOptions,
} from 'tizra/react-query/params'
import {AsyncReturnType, JsonValue} from 'type-fest'
import {useHacks} from './useHack'
import {useHydrating} from './useHydration'

const log = logger('useApi')

/**
 * React hooks for calling the Tizra API with useQuery.
 */
export const useApi = makeUseApi(api, false)
export const useInfiniteApi = makeUseApi(api, true)
export const useAdminApi = makeUseApi(admin, false)
export const useInfiniteAdminApi = makeUseApi(admin, true)
export const useApis = makeUseApis(api)
export const useAdminApis = makeUseApis(admin)

export type UseApiReturnType<
  K extends keyof TizraClient,
  INF extends boolean = false,
> =
  INF extends true ?
    Exclude<
      UseInfiniteQueryResult<
        {pages: AsyncReturnType<TizraClient[K]>[]; pageParams: unknown[]},
        ApiError
      >,
      undefined
    >
  : Exclude<
      UseQueryResult<AsyncReturnType<TizraClient[K]>, ApiError>,
      undefined
    >

export type UseApi<INF extends boolean = false> = {
  [K in keyof TizraClient]: TizraApiParams<K> extends string ?
    (
      firstArg: string | [string] | [string, JsonValue] | Falsy,
      options?: UseApiOptions<K, INF>,
    ) => UseApiReturnType<K, INF>
  : TizraApiParams<K> extends (
    object // i.e. required
  ) ?
    (
      params: TizraApiParams<K> | Falsy,
      options?: UseApiOptions<K, INF>,
    ) => UseApiReturnType<K, INF>
  : (
      params?: TizraApiParams<K> | Falsy | true,
      options?: UseApiOptions<K, INF>,
    ) => UseApiReturnType<K, INF>
}

let alerted = false

const handleApiError = ({
  alert,
  error,
  name,
  reload,
}: {
  alert: boolean
  error: ApiError
  name: keyof TizraClient
  reload: boolean
}) => {
  log.error({alert, alerted, error, name})
  if (!IS_BROWSER) return
  if (!error.status) return // ignore network errors, for now
  if (
    SITE_SUFFIX !== 'admin' &&
    reload &&
    error.status === 403 &&
    (name === 'loggedInUsers' || name === 'search' || name === 'searchTypes') &&
    (navigationType() !== 'reload' || uptimeSeconds() > 30)
  ) {
    window.location.reload()
  } else if (alert && !alerted) {
    alerted = true
    // Short delay to prevent alert from blocking reload.
    setTimeout(() => IS_CHROMATIC || window.alert(error), 1000)
  }
}

const hydratingResultBase = {
  status: 'pending',
  isPending: true,
  isSuccess: false,
  isError: false,
  isLoadingError: false,
  isRefetchError: false,
  data: undefined,
  dataUpdatedAt: 0,
  error: null,
  errorUpdatedAt: 0,
  errorUpdateCount: 0,
  isStale: false,
  isPlaceholderData: false,
  isFetched: false,
  isFetchedAfterMount: false,
  fetchStatus: 'paused',
  isFetching: false,
  isPaused: true,
  isRefetching: false,
  isLoading: false,
  isInitialLoading: false,
  failureCount: 0,
  failureReason: null,
  promise: 'wtf' as any, // not sure how to handle this
} as const satisfies Omit<UseApiReturnType<any>, 'isEnabled' | 'refetch'>

const hydratingResult = <K extends keyof TizraClient>(
  actual: UseApiReturnType<K>,
): UseApiReturnType<K> => ({
  ...hydratingResultBase,
  isEnabled: actual.isEnabled,
  refetch: async () => {
    log.error('someone called hydratingResult.refetch()')
    return hydratingResult(actual)
  },
})

const infiniteHydratingResult = <K extends keyof TizraClient>(
  actual: UseApiReturnType<any, true>,
): UseApiReturnType<K, true> => ({
  ...hydratingResultBase,
  data: {pages: [], pageParams: actual.data?.pageParams ?? []} as any,
  isEnabled: actual.isEnabled,
  isFetchingNextPage: false,
  isFetchNextPageError: false,
  isFetchingPreviousPage: false,
  isFetchPreviousPageError: false,
  fetchNextPage: async () => {
    log.error('someone called infiniteHydratingResult.fetchNextPage()')
    return infiniteHydratingResult(actual)
  },
  fetchPreviousPage: async () => {
    log.error('someone called infiniteHydratingResult.fetchPreviousPage()')
    return infiniteHydratingResult(actual)
  },
  hasNextPage: false,
  hasPreviousPage: false,
  refetch: async () => {
    log.error('someone called infiniteHydratingResult.refetch()')
    return infiniteHydratingResult(actual)
  },
})

/**
 * Make a useApi hook for accessing the Tizra API. This is not exported, because
 * it's only used to define the hooks statically above: useApi, useInfiniteApi,
 * useAdminApi, useInfiniteAdminApi.
 */
function makeUseApi<INF extends boolean>(
  api: TizraClient,
  infinite: INF,
): UseApi<INF> {
  return R.fromPairs(
    R.keys(api).map(<K extends keyof TizraClient>(name: K) => [
      name,
      (...queryArgs: TizraQueryArgs<K, INF>): UseApiReturnType<K, INF> => {
        const {alertOnError, enableDuringHydration} = queryArgs[1] || {}
        const hydrating = useHydrating()
        const params = makeQueryParams<K, INF>({name, api, infinite, queryArgs})
        const query =
          infinite ?
            useInfiniteQuery<any, ApiError>(params as any) // TODO: any
          : useQuery<any, ApiError>(params)
        const hacks = useHacks()
        if (query.error) {
          handleApiError({
            alert:
              typeof alertOnError === 'function' ?
                alertOnError(query.error)
              : (alertOnError ?? hacks.alert),
            error: query.error,
            name,
            reload: hacks.reloadOnApiError,
          })
        }
        return (
          !hydrating || enableDuringHydration ? query
          : infinite ?
            infiniteHydratingResult(query as any) // TODO: any
          : hydratingResult(query)) as UseApiReturnType<K, INF>
      },
    ]),
  ) as UseApi<INF>
}

/**
 * Make a useApis hook for accessing the Tizra API. This is not exported, because
 * it's only used to define the hooks statically above: useApis and useAdminApis.
 */
function makeUseApis(api: TizraClient) {
  return (queriesArgs: Array<[keyof TizraClient, ...any]>) => {
    const hydrating = useHydrating()
    const queries = useQueries<any[], UseQueryResult<unknown, ApiError>[]>({
      // @ts-expect-error something about ApiError versus unknown
      queries: queriesArgs.map(([name, ...queryArgs]) =>
        makeQueryParams({
          name,
          api,
          infinite: false,
          queryArgs: queryArgs as any,
        }),
      ),
    })
    const hacks = useHacks()
    queries.forEach((query, i) => {
      if (query.error) {
        const name = queriesArgs[i][0]
        const {alertOnError} = (queriesArgs[i][2] || {}) as UseApiOptions<
          any,
          false
        >
        handleApiError({
          alert:
            typeof alertOnError === 'function' ?
              alertOnError(query.error)
            : (alertOnError ?? hacks.alert),
          error: query.error,
          name,
          reload: hacks.reloadOnApiError,
        })
      }
    })
    return !hydrating ? queries : (
        queries.map((q, i) => {
          const {enableDuringHydration} = (queriesArgs[i][2] ||
            {}) as UseApiOptions<any, false>
          return enableDuringHydration ? q : hydratingResult(q)
        })
      )
  }
}
